import { createSelector } from 'reselect';
import isObject from 'lodash/isObject';
import { isEmpty, isString } from 'lodash';
import { decompressData } from '../../lib/pako';

/**
 * Retrieves the abstracts array from main.abstractsList.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array|undefined} The list of abstracts, or undefined if not present.
 */
export const abstractsSelector = (state) => state.main?.abstractsList?.abstracts;
/**
 * Retrieves the loading flag for abstracts list from main.abstractsList.
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean|undefined} true if abstracts list is loading, otherwise undefined.
 */
export const isAbstractsLoadingSelector = (state) => state?.main?.abstractsList?.isAbstractsListLoading;
/**
 * Retrieves the integrations object from integrations.integrations.
 *
 * Example shape:
 * {
 *   integrationUuid1: { ... },
 *   integrationUuid2: { ... }
 * }
 *
 * @param {Object} state - The Redux state.
 * @returns {Object|undefined} A map of integrations keyed by their UUID, or undefined if missing.
 */
export const integrationsSelector = (state) => state.integrations?.integrations;
/**
 * Checks if the integrations object from state is not empty.
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean} true if integrations is an object and not empty, otherwise false.
 */
export const isIntegrationsSelector = (state) => !isEmpty(state?.integrations?.integrations);
/**
 * Retrieves the PAASAuth service UUID from main.paas.
 *
 * @param {Object} state - The Redux state.
 * @returns {string|undefined} The PAASAuth service UUID, or undefined if missing.
 */
export const PAASAuthUuidSelector = (state) => state?.main?.paas?.paasAuthServiceUuid;
/**
 * A memoized selector that returns the integrations as an array.
 *
 * If integrations is an object, returns Object.values(integrations).
 * If not an object or undefined, returns an empty array.
 *
 * @function
 * @returns {Function} A reselect selector accepting Redux state, returning an array of integrations.
 */
export const integrationsAsArraySelector = createSelector(integrationsSelector, (integrations) =>
    isObject(integrations) ? Object.values(integrations) : [],
);

/**
 * Determines if the "cloud services" page is in a loading state,
 * by checking multiple flags:
 *  - integrations.isAccountInfoLoading
 *  - integrations.isEnrollmentInProgress
 *  - integrations.isIntegrationsLoading
 *  - main.abstractsList.isAbstractsListLoading
 *  - app.lineLoading
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean} true if any of the above flags is true, false otherwise.
 */
export const isCloudServicesPageLoadingSelector = ({ integrations, app, main }) => {
    const { isAccountInfoLoading, isEnrollmentInProgress, isIntegrationsLoading } = integrations;
    const { lineLoading } = app;
    const isAbstractsListLoading = main?.abstractsList?.isAbstractsListLoading;

    return (
        isAccountInfoLoading || isEnrollmentInProgress || isIntegrationsLoading || isAbstractsListLoading || lineLoading
    );
};
/**
 * Retrieves the `integrations_preview` object from the `integrations` slice of Redux state.
 *
 * Example shape:
 * {
 *   someUuid: {
 *     isLoading: boolean,
 *     data: string,
 *     error: string|undefined
 *   },
 *   anotherUuid: { ... }
 * }
 *
 * @param {Object} state - The Redux state.
 * @returns {Object|undefined} The integrations_preview object, keyed by projectUuid.
 */
export const integrationsPreviewSelector = ({ integrations }) => integrations.integrations_preview;
/**
 * A memoized selector that retrieves a single integration preview entry by its UUID/key.
 *
 * 1. Reads the entire `integrations_preview` object from `integrationsPreviewSelector`.
 * 2. Takes `parjectUuid` as the second argument to the selector.
 * 3. Returns an object with shape: { isLoading, integrationPreview, error }, handling:
 *    - If data is a string, we decompress it via `decompressData`.
 *    - If data is an object, we return it as is.
 *    - If there's an error reading data (exception), we return { integrationPreview: null }.
 *
 * @function
 * @param {Object} state - The Redux state.
 * @param {string} parjectUuid - The UUID/key of the integration preview to retrieve.
 * @returns {Object} An object with:
 *  - `isLoading`: boolean|undefined,
 *  - `integrationPreview`: decompressed data (Object),
 *  - `error`: string|undefined,
 *    or `{ integrationPreview: null }` if an exception occurs.
 */
export const integrationPreviewSelector = createSelector(
    [integrationsPreviewSelector, (_, parjectUuid) => parjectUuid],
    (integrations_preview, parjectUuid) => {
        try {
            const { isLoading, data, error } = integrations_preview?.[parjectUuid] || {};
            if (isString(data)) {
                return { isLoading, integrationPreview: decompressData(data), error };
            }

            return { isLoading, integrationPreview: data, error };
        } catch (e) {
            return { integrationPreview: null };
        }
    },
);

/**
 * Selector to get the `when` block from the selected rule in meshBot.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array<Object>|undefined} An array of trigger blocks or undefined.
 */
export const selectWhen = (state) => state.meshBot?.local?.selectedRule?.when;
/**
 * Selects the serial number of the selected hub from the state.
 * @param {Object} state - The Redux state.
 * @returns {string|null} The serial number of the selected hub, or null if not available.
 */
export const selectedHubSerialSelector = (state) => state?.ezlo?.serial || null;

/**
 * @typedef {Object} HubData
 * @property {Array} settings
 * @property {string} architecture
 * @property {Object} build
 * @property {string} [build.branch]
 * @property {string} [build.builder]
 * @property {string} [build.commit]
 * @property {string} [build.firmware]
 * @property {string} firmware
 * @property {string} kernel
 * @property {Object} localMode
 * @property {Object} localMode.accessKeys
 * @property {string} localtime
 * @property {Object} location
 * @property {number} location.latitude
 * @property {number} location.longitude
 * @property {string} [location.state]
 * @property {string} model
 * @property {boolean} offlineAnonymousAccess
 * @property {boolean} offlineInsecureAccess
 * @property {string} serial
 * @property {Object} support
 * @property {boolean} support.enabled
 * @property {string} support.status
 * @property {string} system
 * @property {string} uptime
 * @property {string} uuid
 * @property {boolean} isEzloData
 * @property {Object} permissions
 * @property {string} permissions.devices
 * @property {string} permissions.ezlo
 * @property {string} permissions.rules
 * @property {Object} info
 * @property {string} info.architecture
 * @property {Object} info.build
 * @property {string} info.firmware
 * @property {Object} properties
 * @property {Array} devices
 * @property {Array} items
 * @property {Array} pages
 * @property {Object} replication
 * @property {Array} rules
 * @property {Array} scenes
 * @property {Array} gateways
 * @property {Array} interfaces
 * @property {Object} favorites
 * @property {Array} favorites.devices
 * @property {Array} favorites.items
 * @property {Array} favorites.rules
 * @property {Array} nodes
 * @property {Object} features
 * @property {Array} expressions
 * @property {boolean} isConnecting
 * @property {boolean} isConnected
 * @property {Object} blockDataList
 * @property {Object} deviceGroups
 * @property {Array} deviceGroups.deviceGroupsList
 * @property {Object} itemGroups
 * @property {Array} itemGroups.itemGroupsList
 */

/**
 * Selects the data object of the selected hub from the state.
 * @param {Object} state - The Redux state.
 * @returns {HubData|null} The data object for the selected hub, or null if not available.
 */
export const selectedHubDataSelector = (state) => state.ezlo?.data?.[state.ezlo.serial] || null;
/**
 * Selects the line loading status from the state.
 * @param {Object} state - The Redux state.
 * @returns {boolean} True if a line is loading, otherwise false.
 */
export const lineLoadingSelector = (state) => state.app?.lineLoading;
/**
 * Selects the connection status of the hub from the state.
 * @param {Object} state - The Redux state.
 * @returns {boolean} True if the hub is connected, otherwise false.
 */
export const isConnectedSelector = (state) => state.ezlo?.isConnected;
/**
 * @typedef {Object} IntegrationAccountInfo
 * @property {string} authentication_type - The authentication type (e.g. "Bearer").
 * @property {string} obfuscated_token - The obfuscated authentication token.
 * @property {string} uuid - The unique identifier of the account.
 */

/**
 * @typedef {Object.<string, IntegrationAccountInfo>} AccountsInfo
 * The object where each key is an account UUID and the value is the account info.
 */

/**
 * Selects the accountsInfo object from the integrations state.
 * @param {Object} state - The Redux state.
 * @returns {AccountsInfo|undefined} The accountsInfo object if present, otherwise undefined.
 */
export const integrationsAccountsInfoSelector = (state) => state.integrations?.accountsInfo;
/**
 * Selects the loading state of integrations from the Redux state.
 * @param {Object} state - The Redux state.
 * @returns {boolean|undefined} True if integrations are loading, false if not, or undefined if not present.
 */
export const isIntegrationsLoadingSelector = (state) => state.integrations?.isIntegrationsLoading;
/**
 * Selects the controllers data object from the Redux state.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object|undefined} The controllers data object, keyed by serial, or undefined if not available.
 *
 * @example
 * const controllersData = useSelector(ezloDataSelector);
 */
export const ezloDataSelector = (state) => state.ezlo?.data;

/**
 * Selects whether the email verification notification has been closed by the user.
 *
 * @param {Object} state - Redux root state
 * @param {Object} state.app - App state slice
 * @param {Object} state.app.emailVerificationState - Email verification state
 * @param {boolean} state.app.emailVerificationState.isClosedNotification - Flag indicating if notification is closed
 * @returns {boolean} True if notification has been closed, false otherwise
 *
 * @example
 * // In a component
 * import { useSelector } from 'react-redux';
 * import { selectIsEmailVerificationNotificationClosed } from './selectors';
 *
 * const MyComponent = () => {
 *   const isNotificationClosed = useSelector(selectIsEmailVerificationNotificationClosed);
 *
 *   if (!isNotificationClosed) {
 *     return <EmailVerificationBanner />;
 *   }
 *   return null;
 * };
 *
 * @example
 * // Direct usage with state
 * const state = {
 *   app: {
 *     emailVerificationState: {
 *       isClosedNotification: true
 *     }
 *   }
 * };
 * const isClosed = selectIsEmailVerificationNotificationClosed(state); // true
 */
export const selectIsEmailVerificationNotificationClosed = ({ app }) =>
    app?.emailVerificationState?.isClosedNotification;
/**
 * Selects whether the user's email has been validated.
 *
 * Checks if the account data has Validated property equal to 1.
 * Uses optional chaining to safely handle cases where account data is not loaded.
 *
 * @param {Object} state - Redux root state
 * @param {Object} state.account - Account state slice
 * @param {Object} [state.account.data] - Account data object (may be undefined)
 * @param {number} [state.account.data.Validated] - Validation status (1 = validated, 0 or undefined = not validated)
 * @returns {boolean} True if email is validated (Validated === 1), false otherwise
 *
 * @example
 * // In a component
 * import { useSelector } from 'react-redux';
 * import { selectIsEmailValidated } from './selectors';
 *
 * const MyComponent = () => {
 *   const isEmailValidated = useSelector(selectIsEmailValidated);
 *
 *   return (
 *     <div>
 *       {isEmailValidated ? (
 *         <VerifiedBadge />
 *       ) : (
 *         <PleaseVerifyEmailMessage />
 *       )}
 *     </div>
 *   );
 * };
 *
 * @example
 * // Direct usage - validated email
 * const state = {
 *   account: {
 *     data: {
 *       Validated: 1,
 *     }
 *   }
 * };
 * const isValidated = selectIsEmailValidated(state); // true
 */
export const selectIsEmailValidated = ({ account }) => account.data?.Validated === 1;

/**
 * Selects the house modes array from the replication data of the selected hub.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array|undefined} Array of mode objects from the API, or undefined if not available.
 *
 * @example
 * const modes = replicationModesSelector(state);
 * // Returns: [{ _id: '1', name: 'Home', armed: false, ... }, ...]
 */
export const replicationModesSelector = (state) => selectedHubDataSelector(state)?.replication?.modes;

/**
 * Selects the partner UUID from the support data slice.
 * Used to determine whether to navigate to the internal support page or open the external support URL.
 *
 * @param {Object} state - The Redux state.
 * @returns {string|undefined} The partner UUID, or undefined if not set.
 */
export const partnerUuidSelector = (state) => state.supportData?.partnerUuid;
